import { EPDProductClassificationId } from 'components/v2/compiler/constants';
import { toaster } from 'components/v2/toast';
import { MAX_FILE_SIZE, MAX_IMG_SIZE } from 'constants/constants';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import {
  ConnectVerifierModel,
  EPDModel,
  FileModel,
  FileParameter,
  PCRModel,
  ProductClassificationModel,
  SendForPublicationEditorialModel,
} from 'services/EpdClient';
import EpdStateService from 'services/EpdStateService';
import EPDService from 'services/EpdService';
import {
  AddEPDFileModel,
  AddEPDPropertyModel,
  CO2UptakeAssociationModel,
  CO2UptakeAssociationModelWithParams,
  CollocationCenterModel,
  ConnectDeveloperModel,
  ConversionFactorModel,
  CpcrPerformanceInformationModel,
  CpcrPerformanceTableDataModel,
  CreateEPDProductionSiteAddressModel,
  DataQualityAssessmentItemModel,
  DataQualityAssessmentReadModel,
  DataQualityAssessmentSummaryModel,
  DeclarationOfCO2AssumptionModelWithParams,
  DeclarationOfCO2Model,
  DepublishReasonType,
  DictionaryCatalogItem,
  DictionaryValue,
  EPDAdditionalInformationModel,
  EPDCoverPageModel,
  EPDDataSourceModel,
  EPDOrganizationInformationAddressModel,
  EPDOrganizationInformationModel,
  EPDProductInformationModel,
  EPDProductMaterialPropertyModel,
  ElectricityDataModel,
  IVersionedEntity,
  InfrastructureAndCapitalGoodsModel,
  LCASpecificationModel,
  LcaSupportModel,
  PCRInformationModel,
  ProductionSiteModel,
  ReferenceFlowModel,
  RequestCollaborationModel,
  ScenarioItemModel,
  ScenarioModuleDefaultDescriptionModel,
  ScenarioModuleItemModel,
  ScenarioModuleReadModel,
  ScrapInputModel,
  ShareOfTotalScrapInputModel,
  SystemBoundaryGridModel,
  SystemBoundaryModel,
  ThirdPartyVerificationModel,
  TransportationPerformanceModel,
  UpdateCollocationCenterModel,
  UpdateDeclaredModulesGeographyModel,
  UpdateDeclaredModulesModel,
  UpdateEPDProductInformationPropertyModel,
  VerificationViewModel,
  BiogasDataModel,
} from 'types/types';
import { OtherDangerMaterialModel } from 'types/content-declaration/OtherDangerMaterialModel';
import { EPDDeclareContentProductModel } from 'types/content-declaration/EPDDeclareContentProductModel';
import { UpdateEPDContentDeclarationProductModel } from 'types/content-declaration/UpdateEPDContentDeclarationProductModel';
import { EPDContentDeclarationProductType } from 'types/content-declaration/EPDContentDeclarationProductType';
import { ProductPackagingMaterialModel } from 'types/content-declaration/ProductPackagingMaterialModel';
import { ProductPanelContentModel } from 'types/content-declaration/ProductPanelContentModel';
import { ProductComponentModel } from 'types/content-declaration/ProductComponentModel';
import { ProductDangerMaterialModel } from 'types/content-declaration/ProductDangerMaterialModel';

import { MutationFunction, UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from './constants';
import {
  addEPDFile,
  addEPDProductInformationFile,
  approveVerification,
  cancelApproveVerification,
  cancelEpdDeregistration,
  cancelUpdateEpd,
  cancelVerification,
  connectCertificateIssuedByBody,
  connectDeveloper,
  connectVerifier,
  copyEpdScenario,
  copyProductInformation,
  createCpcrSpecificTableRow,
  createDictionary,
  createDictionaryValue,
  createEPDProductClassification,
  createEPDProductMaterialProperty,
  createEpdCO2UptakeAssociation,
  createEpdDataQualityAssessmentItem,
  createEpdDataQualityAssessmentSummary,
  createEpdDataSource,
  createEpdDeclarationOfCO2Assumption,
  createEpdDefaultScenarioModuleDescription,
  createEpdElectricity,
  createEpdProductComponent,
  createEpdProductDangerMaterial,
  createEpdProductInformation,
  createEpdProductPackagingMaterial,
  createEpdProductPanelContent,
  createEpdScenario,
  createEpdScenarioModule,
  createEpdScrapInput,
  createEpdShareOfTotalScrapInput,
  createProductionSiteAddress,
  createBiogasData,
  deleteCpcrSpecificDataRow,
  deleteCpcrSpecificDataRows,
  deleteDictionary,
  deleteDictionaryValue,
  deleteEPDProductMaterialProperty,
  deleteEpdDataQualityAssessmentItems,
  deleteEpdDataSource,
  deleteEpdElectricity,
  deleteEpdProductComponents,
  deleteEpdProductDangerMaterials,
  deleteEpdProductPackagingMaterials,
  deleteEpdProductPanelContent,
  deleteEpdScenario,
  deleteEpdScenarioModules,
  deleteEpdScrapInputs,
  deleteProductClassification,
  deleteProductInformation,
  deleteProductionSiteAddress,
  deleteBiogasData,
  depublishEpd,
  deregisterEpd,
  editorialUpdating,
  generateRegistrationCertificate,
  publishEpd,
  regenerateEpdDocument,
  removeEPDFile,
  requestCollaboration,
  sendEpdForPublication,
  sendEpdForPublicationEditorial,
  startVerification,
  unlockEpd,
  updateCpcrSpecificTableColumn,
  updateCpcrSpecificTableRow,
  updateDictionary,
  updateDictionaryValue,
  updateEPDProductMaterialProperty,
  updateEpdElectricity,
  updateEpdCO2UptakeAssociation,
  updateEpdContentDeclarationProduct,
  updateEpdDataQualityAssessmentItem,
  updateEpdDataQualityAssessmentSummary,
  updateEpdDataSource,
  updateEpdDeclarationOfCO2Assumption,
  updateEpdDefaultScenarioModuleDescription,
  updateEpdProductComponent,
  updateEpdProductDangerMaterial,
  updateEpdProductInformation,
  updateEpdProductPackagingMaterial,
  updateEpdProductPanelContent,
  updateEpdPropertyWithMutation,
  updateEpdScenario,
  updateEpdScenarioModule,
  updateEpdScrapInput,
  updateEpdShareOfTotalScrapInput,
  updateEpdSystemBoundaryGridModuleDeclared,
  updateEpdSystemBoundaryGridModuleGeography,
  updateEpdTransportationPerformance,
  updateProductClassification,
  updateProductionSiteAddress,
  updateBiogasData,
  upsertConversionFactor,
  upsertInfrastructureAndCapitalGoods,
  upsertLCASpecification,
  upsertReferenceFlow,
  createEpdProductOtherDangerMaterial,
  updateEpdProductOtherDangerMaterial,
  deleteEpdProductOtherDangerMaterials,
} from './epdApi';
import { createCollocationCenter, deleteCollocationCenter, updateCollocationCenter } from './epdCollocationCentersApi';
import { CustomValidationRuleModel, deleteValidationRule, upsertValidationRule } from './adminValidationRulesApi';
import {
  LCASoftwareCreateModel,
  LCASoftwareVersionCreateModel,
  createLCASoftware,
  createLCASoftwareVersion,
  deleteLCASoftware,
  deleteLCASoftwareVersion,
  updateLCASoftware,
  updateLCASoftwareVersion,
} from './adminLCASoftwareApi';
import { importAdditionalScenariosLcaResultsFile, importLcaResultsFile } from './epdLcaResultsApi';
import useCompilerOptimisticMutation from './useCompilerOptimisticMutation';
import useOptimisticMutation, { OptimisticMutationConfig } from './useOptimisticMutation';
import { EPDDefinitionModel } from 'types/epd-definition/EPDDefinitionModel';
import { AxiosError } from 'axios';
import { putDocumentCollections } from './dynamicFormsApi';
import { getEpdWriteBlockedMessage } from 'util/epdActionErrorUtils';
import { applyEstimatedServiceLifeChange } from 'components/v2/compiler/sections/estimated-service-life/estimatedServiceLifeRules';

// Plain Compiler mutations that the screen fires with mutate() and no own error handling:
// a locked company, a Read-only or a Hidden EPD (409 / 404) shows the server's message.
const notifyEpdWriteBlocked = (error: unknown) => {
  console.error(error);
  const writeBlockedMessage = getEpdWriteBlockedMessage(error);
  if (writeBlockedMessage) {
    toaster({ severity: 'error', summary: 'Error', details: writeBlockedMessage });
  }
};

const reorderFilesByIds = <T extends { id?: string; epdFileId?: string }>(files: T[] | undefined, fileIds: string[]) => {
  return fileIds
    .map((id) => files?.find((file) => file.epdFileId === id || file.id === id))
    .filter((file): file is NonNullable<typeof file> => file !== undefined);
};

const useArrangeFilesMutation = <TQueryData>(
  epdVersionId: string,
  queryKey: string,
  getFiles: (queryData: TQueryData) => { id?: string; epdFileId?: string }[] | undefined,
  setFiles: (queryData: TQueryData, reorderedFiles: ReturnType<typeof reorderFilesByIds>) => TQueryData
) => {
  return useCompilerOptimisticMutation<string[], TQueryData>({
    mutationFn: (fileIds) => EPDService.arrangeEpdFiles(fileIds),
    queryKey: [queryKey, epdVersionId],
    applyOptimisticChanges(queryData, fileIds) {
      const reordered = reorderFilesByIds(getFiles(queryData), fileIds);
      return setFiles(queryData, reordered);
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdGeneralInformation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<AddEPDPropertyModel, EPDDefinitionModel>({
    mutationFn: (data) => updateEpdPropertyWithMutation(data),
    queryKey: [QUERY_KEYS.EPD_DEFINITION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, [args.propertyName]: args.newValue };
    },
    serialScopeId: epdVersionId,
  });
};

// The Estimated service life is saved as ONE object ({ years, description }) under the property
// 'EstimatedServiceLife', so the optimistic update replaces the nested object on the definition.
export const useUpdateEpdEstimatedServiceLife = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<AddEPDPropertyModel, EPDDefinitionModel>({
    mutationFn: (data) => updateEpdPropertyWithMutation(data),
    queryKey: [QUERY_KEYS.EPD_DEFINITION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return applyEstimatedServiceLifeChange(queryData, args.newValue);
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateContentDeclarationProduct = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<UpdateEPDContentDeclarationProductModel, EPDDeclareContentProductModel[]>({
    mutationFn: (data) => updateEpdContentDeclarationProduct(data),
    queryKey: [QUERY_KEYS.BEST_AND_WORST_PRODUCT, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((q) => (q.type === args.type ? { ...q, [args.propertyName]: args.newValue } : q));
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdOrganizationInformation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<AddEPDPropertyModel, EPDOrganizationInformationModel>({
    mutationFn: (data) => updateEpdPropertyWithMutation(data),
    queryKey: [QUERY_KEYS.EPD_ORGANIZATION_INFORMATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, [args.propertyName]: args.newValue };
    },
    serialScopeId: epdVersionId,
  });
};

export const useAddOrganizationInformationFile = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<FormData, EPDOrganizationInformationModel>({
    mutationFn: (data) => addEPDFile(data),
    queryKey: [QUERY_KEYS.EPD_ORGANIZATION_INFORMATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
  });
};

export const useRemoveOrganizationInformationFile = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string, EPDOrganizationInformationModel>({
    mutationFn: (fileId) => removeEPDFile(epdVersionId, fileId),
    queryKey: [QUERY_KEYS.EPD_ORGANIZATION_INFORMATION, epdVersionId],
    applyOptimisticChanges(queryData, fileId) {
      return { ...queryData, organizationImages: queryData.organizationImages?.filter((img) => img.id !== fileId) };
    },
    serialScopeId: epdVersionId,
  });
};

export const useArrangeOrganizationInformationFiles = (epdVersionId: string) => {
  return useArrangeFilesMutation<EPDOrganizationInformationModel>(
    epdVersionId,
    QUERY_KEYS.EPD_ORGANIZATION_INFORMATION,
    (queryData) => queryData.organizationImages,
    (queryData, reorderedFiles) => ({ ...queryData, organizationImages: reorderedFiles })
  );
};

export const useArrangeSystemBoundaryFilesMutation = (epdVersionId: string) => {
  return useArrangeFilesMutation<SystemBoundaryModel>(
    epdVersionId,
    QUERY_KEYS.EPD_SYSTEM_BOUNDARY,
    (queryData) => queryData.processFlowDiagramsImages,
    (queryData, reorderedFiles) => ({ ...queryData, processFlowDiagramsImages: reorderedFiles })
  );
};

export const useUpdateEpdOrganizationInformationAddress = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<AddEPDPropertyModel, EPDOrganizationInformationAddressModel>({
    mutationFn: (data) => updateEpdPropertyWithMutation(data),
    queryKey: [QUERY_KEYS.EPD_ORGANIZATION_INFORMATION_ADDRESS, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, [args.propertyName]: args.newValue };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdProductInformation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<UpdateEPDProductInformationPropertyModel, EPDProductInformationModel[]>({
    mutationFn: (data) => updateEpdProductInformation(data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((p) => (p.id === args.productId ? { ...p, [args.propertyName]: args.newValue } : p));
    },
    serialScopeId: epdVersionId,
  });
};

export const useAddProductInformationFileMutation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<FormData, EPDProductInformationModel[]>({
    mutationFn: (data) => addEPDProductInformationFile(epdVersionId, data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
  });
};

export const useRemoveProductInformationFileMutation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string, EPDProductInformationModel[]>({
    mutationFn: (fileId) => removeEPDFile(epdVersionId, fileId),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION, epdVersionId],
    applyOptimisticChanges(queryData, fileId) {
      return queryData.map((p) => ({
        ...p,
        productDetailsImages: p.productDetailsImages?.filter((img) => img.id !== fileId),
      }));
    },
    serialScopeId: epdVersionId,
  });
};

export const useArrangeProductInformationFilesMutation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<{ productId: string; fileIds: string[] }, EPDProductInformationModel[]>({
    mutationFn: ({ fileIds }) => EPDService.arrangeEpdFiles(fileIds),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((product) => {
        if (product.id !== args.productId) {
          return product;
        }

        const reordered = reorderFilesByIds(product.productDetailsImages, args.fileIds);
        return { ...product, productDetailsImages: reordered };
      });
    },
    serialScopeId: epdVersionId,
  });
};

export const useCreateEpdProductInformation = (epdVersionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => createEpdProductInformation(epdVersionId),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useDeleteProductInformation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProductInformation(id),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useCopyProductInformation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IVersionedEntity) => copyProductInformation(data),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useDeleteProductClassification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProductClassification(id),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useCreateProductClassification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProductClassificationModel) => createEPDProductClassification(data),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useUpdateProductClassification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProductClassificationModel) => updateProductClassification(data),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

//Product material properties

export const useCreateProductMaterialProperty = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => createEPDProductMaterialProperty(productId),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useDeleteProductMaterialProperty = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEPDProductMaterialProperty(id),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useUpdateProductMaterialProperty = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EPDProductMaterialPropertyModel) => updateEPDProductMaterialProperty(data),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

//Address
export const useCreateProductionSiteAddress = (epdVersionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEPDProductionSiteAddressModel) => createProductionSiteAddress(epdVersionId, data),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION, epdVersionId] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useUpdateProductionSiteAddress = (epdVersionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProductionSiteModel) => updateProductionSiteAddress(epdVersionId, data),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION, epdVersionId] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useDeleteProductionSiteAddress = (epdVersionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProductionSiteAddress(epdVersionId, id),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION, epdVersionId] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

// Product components
export const useCreateEpdProductComponent = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<ProductComponentModel, ProductComponentModel[]>({
    mutationFn: (data) => createEpdProductComponent(data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_COMPONENTS, epdVersionId, type],
    applyOptimisticChanges(queryData, args) {
      return [...queryData, args];
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdProductComponent = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<ProductComponentModel, ProductComponentModel[]>({
    mutationFn: (data) => updateEpdProductComponent(data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_COMPONENTS, epdVersionId, type],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((d) => (d.id === args.id ? args : d));
    },
    serialScopeId: epdVersionId,
  });
};

export const useDeleteProductComponents = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useGenericMutation(
    (ids: string[]) => deleteEpdProductComponents(ids),
    [QUERY_KEYS.EPD_PRODUCT_COMPONENTS, epdVersionId, type]
  );
};

// Packaging materials
export const useCreateEpdProductPackagingMaterial = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<ProductPackagingMaterialModel, ProductPackagingMaterialModel[]>({
    mutationFn: (data) => createEpdProductPackagingMaterial(data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_PACKAGING_MATERIALS, epdVersionId, type],
    applyOptimisticChanges(queryData, args) {
      return [...queryData, args];
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdProductPackagingMaterial = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<ProductPackagingMaterialModel, ProductPackagingMaterialModel[]>({
    mutationFn: (data) => updateEpdProductPackagingMaterial(data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_PACKAGING_MATERIALS, epdVersionId, type],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((d) => (d.id === args.id ? args : d));
    },
    serialScopeId: epdVersionId,
  });
};

export const useDeleteProductPackagingMaterials = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<string[], ProductPackagingMaterialModel[]>({
    mutationFn: (ids) => deleteEpdProductPackagingMaterials(ids),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_PACKAGING_MATERIALS, epdVersionId, type],
    applyOptimisticChanges(queryData, ids) {
      return queryData.filter((d) => !d.id || !ids.includes(d.id));
    },
    serialScopeId: epdVersionId,
  });
};

// Panel content
export const useCreateEpdProductPanelContent = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<ProductPanelContentModel, ProductPanelContentModel[]>({
    mutationFn: (data) => createEpdProductPanelContent(data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_PANEL_CONTENT, epdVersionId, type],
    applyOptimisticChanges(queryData, args) {
      return [...queryData, args];
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdProductPanelContent = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<ProductPanelContentModel, ProductPanelContentModel[]>({
    mutationFn: (data) => updateEpdProductPanelContent(data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_PANEL_CONTENT, epdVersionId, type],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((d) => (d.id === args.id ? args : d));
    },
    serialScopeId: epdVersionId,
  });
};

export const useDeleteProductPanelContent = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<string[], ProductPackagingMaterialModel[]>({
    mutationFn: (ids) => deleteEpdProductPanelContent(ids),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_PANEL_CONTENT, epdVersionId, type],
    applyOptimisticChanges(queryData, ids) {
      return queryData.filter((d) => !d.id || !ids.includes(d.id));
    },
    serialScopeId: epdVersionId,
  });
};

// Danger materials
export const useCreateEpdProductDangerMaterial = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<ProductDangerMaterialModel, ProductDangerMaterialModel[]>({
    mutationFn: (data) => createEpdProductDangerMaterial(data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_DANGER_MATERIALS, epdVersionId, type],
    applyOptimisticChanges(queryData, args) {
      return [...queryData, args];
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdProductDangerMaterial = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<ProductDangerMaterialModel, ProductDangerMaterialModel[]>({
    mutationFn: (data) => updateEpdProductDangerMaterial(data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_DANGER_MATERIALS, epdVersionId, type],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((d) => (d.id === args.id ? args : d));
    },
    serialScopeId: epdVersionId,
  });
};

export const useDeleteProductDangerMaterials = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<string[], ProductDangerMaterialModel[]>({
    mutationFn: (ids) => deleteEpdProductDangerMaterials(ids),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_DANGER_MATERIALS, epdVersionId, type],
    applyOptimisticChanges(queryData, ids) {
      return queryData.filter((d) => !d.id || !ids.includes(d.id));
    },
    serialScopeId: epdVersionId,
  });
};

// Other danger materials
export const useCreateEpdProductOtherDangerMaterial = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<OtherDangerMaterialModel, OtherDangerMaterialModel[]>({
    mutationFn: (data) => createEpdProductOtherDangerMaterial(epdVersionId, type, data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_OTHER_DANGER_MATERIALS, epdVersionId, type],
    applyOptimisticChanges(queryData, args) {
      return [...queryData, args];
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdProductOtherDangerMaterial = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<OtherDangerMaterialModel, OtherDangerMaterialModel[]>({
    mutationFn: (data) => updateEpdProductOtherDangerMaterial(epdVersionId, type, data),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_OTHER_DANGER_MATERIALS, epdVersionId, type],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((d) => (d.ord === args.ord ? args : d));
    },
    serialScopeId: epdVersionId,
  });
};

export const useDeleteProductOtherDangerMaterials = (epdVersionId: string, type: EPDContentDeclarationProductType) => {
  return useCompilerOptimisticMutation<number[], OtherDangerMaterialModel[]>({
    mutationFn: (ids) => deleteEpdProductOtherDangerMaterials(epdVersionId, type, ids),
    queryKey: [QUERY_KEYS.EPD_PRODUCT_OTHER_DANGER_MATERIALS, epdVersionId, type],
    applyOptimisticChanges(queryData, ords) {
      return queryData.filter((d) => !ords.includes(d.ord));
    },
    serialScopeId: epdVersionId,
  });
};

// Electricity
export const useCreateEpdElectricity = (epdVersionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ElectricityDataModel) => createEpdElectricity(data),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ELECTRICITY, epdVersionId] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useUpdateEpdElectricity = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<ElectricityDataModel, ElectricityDataModel[]>({
    mutationFn: (data: ElectricityDataModel) => updateEpdElectricity(data),
    queryKey: [QUERY_KEYS.EPD_ELECTRICITY, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((item) => (item.id === args.id ? args : item));
    },
    serialScopeId: epdVersionId,
  });
};

export const useDeleteEpdElectricity = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string, ElectricityDataModel[]>({
    mutationFn: (id: string) => deleteEpdElectricity(id),
    queryKey: [QUERY_KEYS.EPD_ELECTRICITY, epdVersionId],
    applyOptimisticChanges(queryData, id) {
      return queryData.filter((item) => item.id !== id);
    },
    serialScopeId: epdVersionId,
  });
};

// Scenarios
export const useCreateEpdScenario = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ScenarioItemModel) => createEpdScenario(data),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_SCENARIOS] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useUpdateEpdScenario = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ScenarioItemModel) => updateEpdScenario(data),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_SCENARIOS] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useDeleteEpdScenario = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEpdScenario(id),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_SCENARIOS] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useCopyEpdScenario = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IVersionedEntity) => copyEpdScenario(data),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_SCENARIOS] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

// Scenario modules
export const useCreateEpdScenarioModule = (epdVersionId: string, scenarioId: string, module: string) => {
  return useCompilerOptimisticMutation<ScenarioModuleItemModel, ScenarioModuleReadModel>({
    mutationFn: (data) => createEpdScenarioModule(epdVersionId, data),
    queryKey: [QUERY_KEYS.EPD_MODULE, epdVersionId, scenarioId, module],
    applyOptimisticChanges(queryData, args) {
      return {
        ...queryData,
        scenarioModuleItems: [...queryData.scenarioModuleItems, args],
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdScenarioModule = (epdVersionId: string, scenarioId: string, module: string) => {
  return useCompilerOptimisticMutation<ScenarioModuleItemModel, ScenarioModuleReadModel>({
    mutationFn: (data) => updateEpdScenarioModule(epdVersionId, data),
    queryKey: [QUERY_KEYS.EPD_MODULE, epdVersionId, scenarioId, module],
    applyOptimisticChanges(queryData, args) {
      return {
        ...queryData,
        scenarioModuleItems: queryData.scenarioModuleItems.map((s) => (s.ord === args.ord ? args : s)),
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useDeleteEpdScenarioModules = (epdVersionId: string, scenarioId: string, module: string) => {
  return useCompilerOptimisticMutation<string[], ScenarioModuleReadModel>({
    mutationFn: (ids) => deleteEpdScenarioModules(epdVersionId, ids),
    queryKey: [QUERY_KEYS.EPD_MODULE, epdVersionId, scenarioId, module],
    applyOptimisticChanges(queryData, ids) {
      return {
        ...queryData,
        scenarioModuleItems: queryData.scenarioModuleItems.filter((s) => !s.id || !ids.includes(s.id)),
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useCreateEpdScenarioModuleDefaultDescription = (epdVersionId: string, scenarioId: string, module: string) => {
  return useCompilerOptimisticMutation<ScenarioModuleDefaultDescriptionModel, ScenarioModuleReadModel>({
    mutationFn: (data) => createEpdDefaultScenarioModuleDescription(data),
    queryKey: [QUERY_KEYS.EPD_MODULE, epdVersionId, scenarioId, module],
    applyOptimisticChanges(queryData, args) {
      return {
        ...queryData,
        defaultScenarioModuleDescription: args.defaultDescription,
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdScenarioModuleDefaultDescription = (epdVersionId: string, scenarioId: string, module: string) => {
  return useCompilerOptimisticMutation<ScenarioModuleDefaultDescriptionModel, ScenarioModuleReadModel>({
    mutationFn: (data) => updateEpdDefaultScenarioModuleDescription(data),
    queryKey: [QUERY_KEYS.EPD_MODULE, epdVersionId, scenarioId, module],
    applyOptimisticChanges(queryData, args) {
      return {
        ...queryData,
        defaultScenarioModuleDescription: args.defaultDescription,
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEPDClassification = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<AddEPDPropertyModel, EPDDefinitionModel>({
    mutationFn: (data) => updateEpdPropertyWithMutation(data),
    queryKey: [QUERY_KEYS.EPD_DEFINITION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, [args.propertyName]: args.newValue };
    },
    serialScopeId: epdVersionId,
    async onAllSettled({ queryClient, args }) {
      if (args.newValue == EPDProductClassificationId.SINGLE_PRODUCT_ID) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_INFORMATION] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_COMPONENTS] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_PACKAGING_MATERIALS] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_DANGER_MATERIALS] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_PRODUCT_PANEL_CONTENT] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_LCA_CUSTOM_RESULTS] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_LCA_RESULTS] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_LCA_RESULTS_ADDITIONAL] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_CPCR_SPECIFIC_DATA] }),
        ]);
      }
    },
  });
};

export const useChangeEpdTags = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<AddEPDPropertyModel, EPDDefinitionModel>({
    mutationFn: (data) => updateEpdPropertyWithMutation(data),
    queryKey: [QUERY_KEYS.EPD_DEFINITION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return {
        ...queryData,
        [args.propertyName]: args.newValue,
      };
    },
    onAllSettled({ queryClient }) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TAG_SOURCE], exact: true });
    },
    serialScopeId: epdVersionId,
  });
};

export const useChangeEPDVerificationReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddEPDPropertyModel) => updateEpdPropertyWithMutation(data),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_VERIFICATION_REPORT_INFO] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DEFINITION] }),
        ]);
      }
    },
  });
};

export const useImportLcaResultsFile = (epdVersionId: string, onAfterSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormData) => importLcaResultsFile(epdVersionId, data),
    onSettled: async (_data, error) => {
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_LCA_RESULTS] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] }),
      ]);
      if (!error) {
        onAfterSuccess?.();
      }
    },
  });
};

export const useImportAdditionalScenariosLcaResultsFile = (
  epdVersionId: string,
  customResultId: string,
  onAfterSuccess?: () => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormData) => importAdditionalScenariosLcaResultsFile(epdVersionId, customResultId, data),
    onSettled: async (_data, error) => {
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_LCA_CUSTOM_RESULTS_DATA, customResultId] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] }),
      ]);
      if (!error) {
        onAfterSuccess?.();
      }
    },
  });
};

export const useAddEpdVerificationReportFile = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<FormData, VerificationViewModel>({
    mutationFn: (data) => addEPDFile(data),
    queryKey: [QUERY_KEYS.EPD_VERIFICATION_REPORT_INFO, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
  });
};

export const useRemoveEpdVerificationReportFile = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string, VerificationViewModel>({
    mutationFn: (fileId) => removeEPDFile(epdVersionId, fileId),
    queryKey: [QUERY_KEYS.EPD_VERIFICATION_REPORT_INFO, epdVersionId],
    applyOptimisticChanges(queryData, fileId) {
      return { ...queryData, verificationReports: queryData.verificationReports.filter((r) => r.id !== fileId) };
    },
    serialScopeId: epdVersionId,
  });
};

// Declaration of CO2
export const useCreateEpdDeclarationOfCO2Assumption = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<DeclarationOfCO2AssumptionModelWithParams, DeclarationOfCO2Model[]>({
    mutationFn: (data) =>
      createEpdDeclarationOfCO2Assumption(data.epdVersionId, data.nameId, {
        assumptions: data.assumptions,
        isSelected: data.isSelected,
      }),
    queryKey: [QUERY_KEYS.EPD_DECLARATIONS_OF_CO2, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((d) => (d.nameId === args.nameId ? { ...d, ...args } : d));
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdDeclarationOfCO2Assumption = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<DeclarationOfCO2AssumptionModelWithParams, DeclarationOfCO2Model[]>({
    mutationFn: (data) =>
      updateEpdDeclarationOfCO2Assumption(data.epdVersionId, data.nameId, {
        assumptions: data.assumptions,
        isSelected: data.isSelected,
      }),
    queryKey: [QUERY_KEYS.EPD_DECLARATIONS_OF_CO2, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((d) => (d.nameId === args.nameId ? { ...d, ...args } : d));
    },
    serialScopeId: epdVersionId,
  });
};

export const useCreateEpdCO2UptakeAssociation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<CO2UptakeAssociationModelWithParams, CO2UptakeAssociationModel>({
    mutationFn: (data) =>
      createEpdCO2UptakeAssociation(data.epdVersionId, {
        association: data.association,
      }),
    queryKey: [QUERY_KEYS.EPD_CO2_UPTAKE_ASSOCIATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, ...args };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdCO2UptakeAssociation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<CO2UptakeAssociationModelWithParams, CO2UptakeAssociationModel>({
    mutationFn: (data) => updateEpdCO2UptakeAssociation(data.epdVersionId, { id: data.id, association: data.association }),
    queryKey: [QUERY_KEYS.EPD_CO2_UPTAKE_ASSOCIATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, ...args };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpsertLCASpecification = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<LCASpecificationModel, LCASpecificationModel>({
    mutationFn: (data) => upsertLCASpecification(epdVersionId, data),
    queryKey: [QUERY_KEYS.EPD_LCA_SPECIFICATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return args;
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpsertInfrastructureAndCapitalGoods = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<InfrastructureAndCapitalGoodsModel, InfrastructureAndCapitalGoodsModel>({
    mutationFn: (data) => upsertInfrastructureAndCapitalGoods(epdVersionId, data),
    queryKey: [QUERY_KEYS.EPD_INFRASTRUCTURE_AND_CAPITAL_GOODS, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return args;
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpsertEpdReferenceFlow = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<ReferenceFlowModel, ReferenceFlowModel>({
    mutationFn: (data) => upsertReferenceFlow(epdVersionId, data),
    queryKey: [QUERY_KEYS.EPD_REFERENCE_FLOW, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return args;
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpsertEpdConversionFactor = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<ConversionFactorModel, ConversionFactorModel>({
    mutationFn: (data) => upsertConversionFactor(epdVersionId, data),
    queryKey: [QUERY_KEYS.EPD_CONVERSION_FACTOR, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return args;
    },
    serialScopeId: epdVersionId,
  });
};

export const useCreateEpdDataSource = (epdVersionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EPDDataSourceModel) => createEpdDataSource(epdVersionId, data),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DATA_SOURCES] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useUpdateEpdDataSource = (epdVersionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EPDDataSourceModel) => updateEpdDataSource(epdVersionId, data),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DATA_SOURCES] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

export const useDeleteEpdDataSource = (epdVersionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEpdDataSource(id, epdVersionId),
    onMutate: () => {},
    onError: notifyEpdWriteBlocked,
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DATA_SOURCES] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
      }
    },
  });
};

// Contribution of scrap inputs
export const useCreateEpdScrapInput = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<ScrapInputModel, ScrapInputModel[]>({
    mutationFn: (data) => createEpdScrapInput(data),
    queryKey: [QUERY_KEYS.EPD_SCRAP_INPUTS, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return [...queryData, args];
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdScrapInput = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<ScrapInputModel, ScrapInputModel[]>({
    mutationFn: (data) => updateEpdScrapInput(data),
    queryKey: [QUERY_KEYS.EPD_SCRAP_INPUTS, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((d) => (d.ord === args.ord ? args : d));
    },
    serialScopeId: epdVersionId,
  });
};

export const useDeleteEpdScrapInputs = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string[], ScrapInputModel[]>({
    mutationFn: (ids) => deleteEpdScrapInputs(ids),
    queryKey: [QUERY_KEYS.EPD_SCRAP_INPUTS, epdVersionId],
    applyOptimisticChanges(queryData, ids) {
      return queryData.filter((d) => !d.id || !ids.includes(d.id));
    },
    serialScopeId: epdVersionId,
  });
};

export const useCreateEpdShareOfTotalScrapInput = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<ShareOfTotalScrapInputModel, ShareOfTotalScrapInputModel>({
    mutationFn: (data) => createEpdShareOfTotalScrapInput(data),
    queryKey: [QUERY_KEYS.EPD_SHARE_OF_TOTAL_SCRAP_INPUT, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdShareOfTotalScrapInput = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<ShareOfTotalScrapInputModel, ShareOfTotalScrapInputModel>({
    mutationFn: (data) => updateEpdShareOfTotalScrapInput(data),
    queryKey: [QUERY_KEYS.EPD_SHARE_OF_TOTAL_SCRAP_INPUT, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return args;
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdSystemBoundaryModuleDeclared = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<UpdateDeclaredModulesModel, SystemBoundaryGridModel>({
    mutationFn: (data) => updateEpdSystemBoundaryGridModuleDeclared(data),
    queryKey: [QUERY_KEYS.EPD_LCA_DECLARED_MODULES, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return {
        ...queryData,
        declaredModules: queryData.declaredModules
          ? {
              ...queryData.declaredModules,
              [args.fieldName]: args.value,
            }
          : queryData.declaredModules,
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdSystemBoundaryModuleGeography = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<UpdateDeclaredModulesGeographyModel, SystemBoundaryGridModel>({
    mutationFn: (data) => updateEpdSystemBoundaryGridModuleGeography(data),
    queryKey: [QUERY_KEYS.EPD_LCA_DECLARED_MODULES, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return {
        ...queryData,
        declaredModulesGeography: queryData.declaredModulesGeography
          ? {
              ...queryData.declaredModulesGeography,
              [args.fieldName]: args.value,
            }
          : queryData.declaredModulesGeography,
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdTransportationPerformance = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<TransportationPerformanceModel, TransportationPerformanceModel>({
    mutationFn: (data) => updateEpdTransportationPerformance(data),
    queryKey: [QUERY_KEYS.EPD_LCA_TRANSPORTATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return args;
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateSystemBoundaryMutation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<AddEPDPropertyModel, SystemBoundaryModel>({
    mutationFn: (data) => updateEpdPropertyWithMutation(data),
    queryKey: [QUERY_KEYS.EPD_SYSTEM_BOUNDARY, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      switch (args.propertyName) {
        case 'systemBoundaryDescription':
          return { ...queryData, description: args.newValue };
        default:
          return { ...queryData, [args.propertyName]: args.newValue };
      }
    },
    serialScopeId: epdVersionId,
  });
};

export const useAddSystemBoundaryFileMutation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<FormData, SystemBoundaryModel>({
    mutationFn: (data) => addEPDFile(data),
    queryKey: [QUERY_KEYS.EPD_SYSTEM_BOUNDARY, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
  });
};

export const useRemoveSystemBoundaryFileMutation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string, SystemBoundaryModel>({
    mutationFn: (fileId) => removeEPDFile(epdVersionId, fileId),
    queryKey: [QUERY_KEYS.EPD_SYSTEM_BOUNDARY, epdVersionId],
    applyOptimisticChanges(queryData, fileId) {
      return {
        ...queryData,
        processFlowDiagramsImages: queryData.processFlowDiagramsImages.filter((img) => img.id !== fileId),
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useAddCoverPageFile = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<FormData, EPDCoverPageModel>({
    mutationFn: (data) => addEPDFile(data),
    queryKey: [QUERY_KEYS.EPD_COVER_PAGE_INFORMATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
  });
};

export const useRemoveCoverPageFile = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string, EPDCoverPageModel>({
    mutationFn: (fileId) => removeEPDFile(epdVersionId, fileId),
    queryKey: [QUERY_KEYS.EPD_COVER_PAGE_INFORMATION, epdVersionId],
    applyOptimisticChanges(queryData, fileId) {
      return {
        ...queryData,
        coverPageProductImage: queryData.coverPageProductImage?.id === fileId ? undefined : queryData.coverPageProductImage,
        companyLogoImages: queryData.companyLogoImages?.filter((p) => p.id !== fileId),
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdPCRInformation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<
    { requestData: AddEPDPropertyModel; newOptimisticValue?: PCRModel },
    PCRInformationModel
  >({
    mutationFn: ({ requestData }) => updateEpdPropertyWithMutation(requestData),
    queryKey: [QUERY_KEYS.EPD_PCR_INFORMATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      switch (args.requestData.propertyName) {
        case 'PCR':
          return { ...queryData, pcr: args.newOptimisticValue };
        case 'CPCR':
          return { ...queryData, cpcr: args.newOptimisticValue };
        case 'cpcrProductCategory': {
          const newCategoryId = args.requestData.newValue;
          const possibleCategories = queryData.cpcr?.cpcrProductCategories;
          if (!possibleCategories) {
            return undefined;
          }
          return {
            ...queryData,
            cpcrProductCategory: newCategoryId ? possibleCategories.find((c) => c.id === newCategoryId) : undefined,
          };
        }
        default:
          throw new Error('Not implemented');
      }
    },
    onAllSettled({ queryClient }) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_CPCR_SPECIFIC_DATA, epdVersionId], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_LCA_SPECIFICATION, epdVersionId], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DEFINITION, epdVersionId], exact: true });
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdLcaSupport = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<AddEPDPropertyModel, LcaSupportModel>({
    mutationFn: (data) => updateEpdPropertyWithMutation(data),
    queryKey: [QUERY_KEYS.EPD_LCA_SUPPORT, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdThirdPartyVerification = (
  epdVersionId: string,
  onError?: OptimisticMutationConfig<AddEPDPropertyModel, ThirdPartyVerificationModel>['onError']
) => {
  return useCompilerOptimisticMutation<AddEPDPropertyModel, ThirdPartyVerificationModel>({
    mutationFn: (data) => updateEpdPropertyWithMutation(data),
    queryKey: [QUERY_KEYS.EPD_THIRD_PARTY_VERIFICATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      const dontOptimisticallyUpdateProperties = ['epdVerifier', 'preverifiedToolVersion', 'processCertificateIssuedBy'];
      if (dontOptimisticallyUpdateProperties.includes(args.propertyName)) {
        return undefined;
      }
      if (args.propertyName === 'preverifiedTool') {
        const selectedTool = args.newValue
          ? queryData.preVerifiedToolDataSource?.find((t) => t.value === args.newValue)
          : null;
        if (selectedTool === undefined) {
          return undefined;
        }
        return {
          ...queryData,
          preverifiedTool: { id: selectedTool!.value, name: selectedTool!.label, toolType: 0 },
        };
      }
      return { ...queryData, [args.propertyName]: args.newValue };
    },
    onAllSettled({ queryClient }) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_COMPANY_VERIFIERS] });
    },
    onError,
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdAdditionalInformation = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<AddEPDPropertyModel, EPDAdditionalInformationModel>({
    mutationFn: (data) => updateEpdPropertyWithMutation(data),
    queryKey: [QUERY_KEYS.EPD_ADDITIONAL_INFORMATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, [args.propertyName]: args.newValue };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdProperty = (epdId: string) => {
  return useOptimisticMutation<AddEPDPropertyModel, EPDModel>({
    mutationFn: (data) => updateEpdPropertyWithMutation(data),
    queryKey: [QUERY_KEYS.EPD_MODEL, epdId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, [args?.propertyName as string]: args.newValue };
    },
    serialScopeId: epdId,
    onError: (error) => {
      const errorMessage = error?.response?.data?.detail || 'Data update error';
      toaster({ severity: 'error', summary: 'Error', details: '' + errorMessage });
    },
  });
};

export const useConnectEpdLcaPractitioner = (
  epdVersionId: string,
  onError?: OptimisticMutationConfig<ConnectDeveloperModel, LcaSupportModel>['onError']
) => {
  return useCompilerOptimisticMutation<ConnectDeveloperModel, LcaSupportModel>({
    mutationFn: (data) => connectDeveloper(data),
    queryKey: [QUERY_KEYS.EPD_LCA_SUPPORT, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
    onError,
  });
};

export const useConnectEpdIndividualVerifier = (
  epdVersionId: string,
  onError?: OptimisticMutationConfig<ConnectVerifierModel, ThirdPartyVerificationModel>['onError']
) => {
  return useCompilerOptimisticMutation<ConnectVerifierModel, ThirdPartyVerificationModel>({
    mutationFn: (data) => connectVerifier(data),
    queryKey: [QUERY_KEYS.EPD_THIRD_PARTY_VERIFICATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
    onError,
  });
};

export const useConnectEpdCertificationBody = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<any, ThirdPartyVerificationModel>({
    mutationFn: (data) => connectCertificateIssuedByBody(data),
    queryKey: [QUERY_KEYS.EPD_THIRD_PARTY_VERIFICATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
    onError: (error) => {
      const errorMessage =
        error?.response?.status === 403 ? 'Only the EPD Owner is allowed to perform this action.' : 'Data update error';
      toaster({ severity: 'error', summary: 'Action not allowed', details: errorMessage });
    },
  });
};

export const useRequestCollaboration = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<RequestCollaborationModel, ThirdPartyVerificationModel>({
    mutationFn: (data) => requestCollaboration(data),
    queryKey: [QUERY_KEYS.EPD_THIRD_PARTY_VERIFICATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
  });
};

export const useAddEPDProcessCertificate = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<FormData, ThirdPartyVerificationModel>({
    mutationFn: (data) => addEPDFile(data),
    queryKey: [QUERY_KEYS.EPD_THIRD_PARTY_VERIFICATION, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
  });
};

export const useRemoveEPDProcessCertificate = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string, ThirdPartyVerificationModel>({
    mutationFn: (data) => removeEPDFile(epdVersionId, data),
    queryKey: [QUERY_KEYS.EPD_THIRD_PARTY_VERIFICATION, epdVersionId],
    applyOptimisticChanges(queryData, fileId) {
      return { ...queryData, processCertificates: queryData.processCertificates.filter((c) => c.id !== fileId) };
    },
    serialScopeId: epdVersionId,
  });
};

export const useAddEpdAdditionalDocument = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<FormData, FileModel[]>({
    mutationFn: (data) => addEPDFile(data),
    queryKey: [QUERY_KEYS.EPD_ADDITIONAL_DOCUMENTS, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
  });
};

export const useRemoveEpdAdditionalDocument = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string, FileModel[]>({
    mutationFn: (data) => removeEPDFile(epdVersionId, data),
    queryKey: [QUERY_KEYS.EPD_ADDITIONAL_DOCUMENTS, epdVersionId],
    applyOptimisticChanges(queryData, fileId) {
      return queryData.filter((f) => f.id !== fileId);
    },
  });
};

export const addEpdFile = async (data: AddEPDFileModel, mutation: UseMutationResult<any, Error, any, any>) => {
  if (!data) {
    throw new Error('Required data parameter must not be null.');
  }

  const { epdId, propertyName, file, epdProductInformationId, documentTypeId, documentLanguageId } = data;

  if (!epdId || !propertyName || !file) {
    throw new Error('Parameters epdId, propertyName, file must be specified.');
  }

  const fileName = file.name;
  const fileType = file.type;

  if (fileType.startsWith('image/') && file.size > MAX_IMG_SIZE) {
    toaster({
      severity: 'error',
      summary: 'Error',
      details: t('epdWizard.messages.errorTooLargeImageFile', {
        size: Math.floor(MAX_IMG_SIZE / (1024 * 1024)),
      }) as string,
    });

    return;
  }

  const reader = new FileReader();
  let resolveUploading: (value?: undefined) => void;
  let rejectUploading: (reason?: any) => void;
  const uploadingPromise = new Promise((resolve, reject) => {
    resolveUploading = resolve;
    rejectUploading = reject;
  });
  reader.onload = async () => {
    const fileBlob = reader.result as ArrayBuffer;
    if (fileBlob) {
      const blob = new Blob([new Uint8Array(fileBlob)], { type: fileType });
      if (blob.size > MAX_FILE_SIZE) {
        toast.error(t('epdWizard.messages.errorTooLargeFile') as string, {
          position: 'top-center',
        });
        rejectUploading(new Error('File is too large.'));
        return;
      }

      try {
        const addFileModel: FileParameter = { fileName, data: blob };
        const addFileFormData = new FormData();

        addFileFormData.append('EPDId', epdId.toString());
        addFileFormData.append('PropertyName', propertyName.toString());
        addFileFormData.append('NewValue', file);

        if (!!documentTypeId) {
          addFileFormData.append('DocumentTypeId', documentTypeId);
        }

        if (!!documentLanguageId) {
          addFileFormData.append('DocumentLanguageId', documentLanguageId);
        }

        if (!!epdProductInformationId) {
          addFileFormData.append('EPDProductInformationId', epdProductInformationId.toString());
        }

        await mutation.mutateAsync(addFileFormData);

        resolveUploading();
      } catch (e) {
        toaster({
          severity: 'error',
          summary: 'Error',
          details: t('epdWizard.messages.error') as string,
        });
        rejectUploading(e);
      }
    }
  };
  reader.readAsArrayBuffer(file);
  await uploadingPromise;
};

export const useCreateDictionary = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DictionaryCatalogItem) => createDictionary(data),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DICTIONARY_CATALOG] });
      }
    },
  });
};

export const useUpdateDictionary = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DictionaryCatalogItem) => updateDictionary(data),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DICTIONARY_CATALOG] });
      }
    },
  });
};

export const useCreateDictionaryValue = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DictionaryValue) => createDictionaryValue(data),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DICTIONARY_VALUES] });
      }
    },
  });
};

export const useUpdateDictionaryValue = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DictionaryValue) => updateDictionaryValue(data),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DICTIONARY_VALUES] });
      }
    },
  });
};

export const useDeleteDictionary = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DictionaryCatalogItem) => deleteDictionary(data),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DICTIONARY_CATALOG] });
      }
    },
  });
};

export const useDeleteDictionaryValue = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDictionaryValue(id),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DICTIONARY_VALUES] });
      }
    },
  });
};

export const useCreateCollocationCenter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CollocationCenterModel) => createCollocationCenter(data),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COLLOCATION_CENTER] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ORGANIZATION_INFORMATION] });
      }
    },
  });
};

export const useUpdateCollocationCenter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateCollocationCenterModel) => updateCollocationCenter(data),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COLLOCATION_CENTER] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ORGANIZATION_INFORMATION] });
      }
    },
  });
};

export const useDeleteCollocationCenter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCollocationCenter(id),
    onMutate: () => {},
    onError: () => {
      console.error('error');
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COLLOCATION_CENTER] });
      }
    },
  });
};

function useActionButtonMutation<TArgs>(epdId: string, epdVersionId: string, mutationFn: (args: TArgs) => Promise<any>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: async () => {
      // Note: must await the following queries, so the spinner keeps spinning until all data is refreshed
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_LATEST_VERSION, epdId], exact: true }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES, epdId], exact: true }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_DEFINITION, epdVersionId], exact: true }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_VERIFICATION_REPORT_INFO, epdVersionId], exact: true }),

        // The PDF document shows up in the additional info tab. Ed is going to promote deletion of this feature, but until then, let's revalidate files properly
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ADDITIONAL_DOCUMENTS, epdVersionId], exact: true }),
      ]);
    },
  });
}

export const useCancelUpdateEpd = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<void>(epdId, epdVersionId, () => cancelUpdateEpd(epdId));
};

export const useStartVerification = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<void>(epdId, epdVersionId, () => startVerification(epdId));
};

export const useCancelVerification = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<void>(epdId, epdVersionId, () => cancelVerification(epdId));
};

export const useSendEpdForPublication = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<void>(epdId, epdVersionId, () => sendEpdForPublication(epdId));
};

export const useCancelPublicationEpd = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<void>(epdId, epdVersionId, () => EpdStateService.cancelPublication(epdId));
};

export const useDeregisterEpd = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<void>(epdId, epdVersionId, () => deregisterEpd(epdId));
};

export const useCancelEpdDeregistration = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<void>(epdId, epdVersionId, () => cancelEpdDeregistration(epdId));
};

export const useUnlockEpd = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<void>(epdId, epdVersionId, () => unlockEpd(epdId));
};

export const useApproveVerification = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<FormData>(epdId, epdVersionId, (approveModel) => approveVerification(epdId, approveModel));
};

export const useDeclineVerification = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<{ reason: string }>(epdId, epdVersionId, (model) =>
    EpdStateService.declineVerification(epdId, model.reason)
  );
};

export const useCancelApproveVerificationMutation = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<void>(epdId, epdVersionId, () => cancelApproveVerification(epdId));
};

export const usePublishEpd = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<string>(epdId, epdVersionId, (versionId) => publishEpd(epdId, versionId));
};

export const useRegenerateEpdDocument = (epdId: string, epdVersionId: string) => {
  return useGenericMutation(() => regenerateEpdDocument(epdId, epdVersionId), [[QUERY_KEYS.EPD_ADDITIONAL_DOCUMENTS]]);
};

export const useGenerateRegistrationCertificate = (epdId: string, epdVersionId: string) => {
  return useGenericMutation(
    () => generateRegistrationCertificate(epdId),
    [[QUERY_KEYS.EPD_ADDITIONAL_DOCUMENTS, epdVersionId]]
  );
};

export const useDeclinePublicationEpd = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<{ declineReason: string }>(epdId, epdVersionId, ({ declineReason }) =>
    EpdStateService.declinePublication(epdId, declineReason)
  );
};

export const useDepublishEpd = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<{ depublicationReason: string | null; depublishType: DepublishReasonType | null }>(
    epdId,
    epdVersionId,
    ({ depublicationReason, depublishType }) => depublishEpd(epdId, depublicationReason, depublishType)
  );
};

export const useLicenseeAdminSendToPublish = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<{ licenseeAdminReviewComment: string }>(
    epdId,
    epdVersionId,
    ({ licenseeAdminReviewComment }) => EpdStateService.licenseeAdminSendToPublish(epdId, licenseeAdminReviewComment)
  );
};

export const useSendEpdForPublicationEditorial = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<SendForPublicationEditorialModel>(epdId, epdVersionId, (data) =>
    sendEpdForPublicationEditorial(data)
  );
};

export const useEditorialUpdating = (epdId: string, epdVersionId: string) => {
  return useActionButtonMutation<void>(epdId, epdVersionId, () => editorialUpdating(epdId));
};

/***********************************************************************/
export const useGenericMutation = <TData = unknown, TVariables = void>(
  mutationFn: MutationFunction<TData, TVariables>,
  invalidateQueriesKeys: (string | unknown[])[]
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onMutate: () => {},
    onError: (error: any) => {
      console.error('Error occurred:', error);
    },
    onSuccess: () => {},
    onSettled: async (_, error) => {
      if (!error) {
        await Promise.all([
          ...invalidateQueriesKeys.map((key) =>
            queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] })
          ),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] }),
        ]);
      }
    },
  });
};

export const useCreateCpcrSpecificTableRow = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<CpcrPerformanceTableDataModel, CpcrPerformanceInformationModel>({
    mutationFn: (data) => createCpcrSpecificTableRow(data, epdVersionId),
    queryKey: [QUERY_KEYS.EPD_CPCR_SPECIFIC_DATA, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, data: [...queryData.data, args] };
    },
    serialScopeId: epdVersionId,
  });
};

export const useDeleteCpcrSpecificTableRow = () => {
  return useGenericMutation((id: string) => deleteCpcrSpecificDataRow(id), [[QUERY_KEYS.EPD_CPCR_SPECIFIC_DATA]]);
};

export const useDeleteCpcrSpecificTableRows = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string[], CpcrPerformanceInformationModel>({
    mutationFn: (ids) => deleteCpcrSpecificDataRows(ids),
    queryKey: [QUERY_KEYS.EPD_CPCR_SPECIFIC_DATA, epdVersionId],
    applyOptimisticChanges(queryData, ids) {
      return { ...queryData, data: queryData.data.filter((d) => !d.id || !ids.includes(d.id)) };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateCpcrSpecificTableRow = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<CpcrPerformanceTableDataModel, CpcrPerformanceInformationModel>({
    mutationFn: (data) => updateCpcrSpecificTableRow(data, epdVersionId),
    queryKey: [QUERY_KEYS.EPD_CPCR_SPECIFIC_DATA, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return {
        ...queryData,
        data: queryData.data.map((d) => (d.tableCode === args.tableCode && d.ord === args.ord ? args : d)),
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateCpcrSpecificTableColumn = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<any, CpcrPerformanceInformationModel>({
    mutationFn: (data) => updateCpcrSpecificTableColumn(data),
    queryKey: [QUERY_KEYS.EPD_CPCR_SPECIFIC_DATA, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
  });
};

export const useCreateEpdDataQualityAssessmentItem = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<DataQualityAssessmentItemModel, DataQualityAssessmentReadModel>({
    mutationFn: (data) => createEpdDataQualityAssessmentItem(data),
    queryKey: [QUERY_KEYS.EPD_DATA_QUALITY_ASSESSMENT_AND_REFERENCE_YEARS, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, dataQualityAssessmentItems: [...queryData.dataQualityAssessmentItems, args] };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdDataQualityAssessmentItem = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<DataQualityAssessmentItemModel, DataQualityAssessmentReadModel>({
    mutationFn: (data) => updateEpdDataQualityAssessmentItem(data),
    queryKey: [QUERY_KEYS.EPD_DATA_QUALITY_ASSESSMENT_AND_REFERENCE_YEARS, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return {
        ...queryData,
        dataQualityAssessmentItems: queryData.dataQualityAssessmentItems.map((it) => (it.ord === args.ord ? args : it)),
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useDeleteEpdDataQualityAssessmentItems = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string[], DataQualityAssessmentReadModel>({
    mutationFn: (ids) => deleteEpdDataQualityAssessmentItems(epdVersionId, ids),
    queryKey: [QUERY_KEYS.EPD_DATA_QUALITY_ASSESSMENT_AND_REFERENCE_YEARS, epdVersionId],
    applyOptimisticChanges(queryData, ids) {
      return {
        ...queryData,
        dataQualityAssessmentItems: queryData.dataQualityAssessmentItems.filter((it) => !it.id || !ids.includes(it.id)),
      };
    },
    serialScopeId: epdVersionId,
  });
};

export const useCreateEpdDataQualityAssessmentSummary = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<DataQualityAssessmentSummaryModel, DataQualityAssessmentReadModel>({
    mutationFn: (data) => createEpdDataQualityAssessmentSummary(data),
    queryKey: [QUERY_KEYS.EPD_DATA_QUALITY_ASSESSMENT_AND_REFERENCE_YEARS, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, dataQualityAssessmentSummary: args };
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateEpdDataQualityAssessmentSummary = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<DataQualityAssessmentSummaryModel, DataQualityAssessmentReadModel>({
    mutationFn: (data) => updateEpdDataQualityAssessmentSummary(data),
    queryKey: [QUERY_KEYS.EPD_DATA_QUALITY_ASSESSMENT_AND_REFERENCE_YEARS, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return { ...queryData, dataQualityAssessmentSummary: args };
    },
    serialScopeId: epdVersionId,
  });
};

// Biogas data
export const useCreateBiogasData = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<BiogasDataModel, BiogasDataModel>({
    mutationFn: (data) => createBiogasData(data),
    queryKey: [QUERY_KEYS.EPD_BIOGAS, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return undefined;
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpdateBiogasData = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<BiogasDataModel, BiogasDataModel[]>({
    mutationFn: (data) => updateBiogasData(data),
    queryKey: [QUERY_KEYS.EPD_BIOGAS, epdVersionId],
    applyOptimisticChanges(queryData, args) {
      return queryData.map((d) => (d.id === args.id ? args : d));
    },
    serialScopeId: epdVersionId,
  });
};

export const useDeleteBiogasData = (epdVersionId: string) => {
  return useCompilerOptimisticMutation<string[], BiogasDataModel[]>({
    mutationFn: (ids) => deleteBiogasData(ids),
    queryKey: [QUERY_KEYS.EPD_BIOGAS, epdVersionId],
    applyOptimisticChanges(queryData, ids) {
      return queryData.filter((d) => !d.id || !ids.includes(d.id));
    },
    serialScopeId: epdVersionId,
  });
};

export const useUpsertValidationRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CustomValidationRuleModel) => upsertValidationRule(data),
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_VALIDATION_RULES] });
      }
    },
  });
};

export const useDeleteValidationRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ruleName: string) => deleteValidationRule(ruleName),
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_VALIDATION_RULES] });
      }
    },
  });
};

// LCA Software mutations

export const useCreateLCASoftware = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LCASoftwareCreateModel) => createLCASoftware(data),
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_LCA_SOFTWARE] });
      }
    },
  });
};

export const useUpdateLCASoftware = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string } & LCASoftwareCreateModel) => updateLCASoftware(data.id, data),
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_LCA_SOFTWARE] });
      }
    },
  });
};

export const useDeleteLCASoftware = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteLCASoftware(id),
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_LCA_SOFTWARE] });
      }
    },
  });
};

export const useCreateLCASoftwareVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { softwareId: string } & LCASoftwareVersionCreateModel) =>
      createLCASoftwareVersion(data.softwareId, { name: data.name }),
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_LCA_SOFTWARE] });
      }
    },
  });
};

export const useUpdateLCASoftwareVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { softwareId: string; versionId: string } & LCASoftwareVersionCreateModel) =>
      updateLCASoftwareVersion(data.softwareId, data.versionId, { name: data.name }),
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_LCA_SOFTWARE] });
      }
    },
  });
};

export const useDeleteLCASoftwareVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { softwareId: string; versionId: string }) =>
      deleteLCASoftwareVersion(data.softwareId, data.versionId),
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_LCA_SOFTWARE] });
      }
    },
  });
};

export const useUpdateDocumentCollections = (docId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (collectionIds: string[]) => putDocumentCollections(docId, collectionIds),
    onError: (error: AxiosError<{ detail?: string }>) => {
      const details =
        error?.response?.status === 403
          ? 'You are not allowed to change the collections of this EPD.'
          : error?.response?.data?.detail || 'The collections could not be updated.';
      toaster({ severity: 'error', summary: 'Collections not updated', details });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DYNAMIC_FORMS_DOCUMENT_COLLECTIONS, docId] });
    },
  });
};
